<script setup lang="ts">
import { onMounted, ref } from 'vue';

import { SearchService } from '@/core/sourcing/search/search.service';
import type { RecentSearch } from '@/core/sourcing/search/types/recent-search.type';
import { recentSearchBadge } from '@/core/sourcing/search/utils/recent-search-badge.util';
import { formatRelativeTime } from '@/core/sourcing/utils/dates';
import { pluralize } from '@/utils/strings/pluralize.util';
import { ErrorService } from '@/core/shared/errors/error.service';

import ChevronRightIcon from '@/assets/svg/chevron-right.svg?component';

// The parent owns search execution: a row click asks the view to re-run the stored
// search; an example click asks it to run that text.
const emit = defineEmits<{
  (e: 'rerun', recent: RecentSearch): void;
  (e: 'use-example', text: string): void;
}>();

// Curated prompts are product-owned (David) and hardcoded in ats-ui by design — no backend
// (TDD §2.5).
const CURATED_EXAMPLES = [
  'Maintenance technician near Cincinnati',
  'CNC machinist in Houston active in the last 30 days',
  'TIG welders with AWS certifications near Madison, WI',
  'Quality inspector with aerospace background',
  'Forklift operator within 50 miles of Detroit',
];

const searchService = new SearchService();

const recentSearches = ref<RecentSearch[]>([]);
const isLoading = ref(true);
const loadFailed = ref(false);

onMounted(async () => {
  try {
    recentSearches.value = await searchService.getRecentSearches();
  } catch (error) {
    // Non-blocking: a recents fetch failure degrades to "examples only" (TDD §5.4).
    loadFailed.value = true;
    ErrorService.captureException(error);
  } finally {
    isLoading.value = false;
  }
});

function badge(recent: RecentSearch): string {
  return recentSearchBadge(recent.searchMode, recent.detectedBooleanOperators);
}

function rowText(recent: RecentSearch): string {
  if (recent.searchMode === 'job') {
    const title = recent.documentSearchJobTitle ?? 'job';
    return recent.locationText
      ? `From job: ${title} · ${recent.locationText}`
      : `From job: ${title}`;
  }
  return recent.rawInput ?? recent.query ?? '';
}

function rowMeta(recent: RecentSearch): string {
  const count = `${recent.nbHits.toLocaleString()} ${pluralize(recent.nbHits, 'result')}`;
  const when = formatRelativeTime(recent.createdAt);
  return when ? `${count} · ${when}` : count;
}
</script>

<template>
  <div class="empty" data-test="recent-searches-empty">
    <h1 class="empty-title">Find your next hire</h1>
    <p class="empty-sub">
      Type naturally, paste a job description, or use keywords and Boolean — we'll figure out what
      you meant.
    </p>

    <div class="recent-block">
      <!-- Recent searches: hidden entirely when the fetch failed (examples-only fallback). -->
      <template v-if="!loadFailed">
        <div class="recent-title">Recent searches</div>

        <div v-if="isLoading" class="recent-state" data-test="recent-searches-loading">
          Loading your recent searches…
        </div>

        <template v-else-if="recentSearches.length">
          <button
            v-for="recent in recentSearches"
            :key="recent.id"
            type="button"
            class="recent-item"
            data-test="recent-search-row"
            @click="emit('rerun', recent)"
          >
            <span class="recent-q">
              <span
                class="mode-tag"
                :class="`mode-tag--${badge(recent).toLowerCase()}`"
                data-test="recent-search-badge"
                >{{ badge(recent) }}</span
              >
              <span data-test="recent-search-text">{{ rowText(recent) }}</span>
            </span>
            <span class="recent-meta" data-test="recent-search-meta">{{ rowMeta(recent) }}</span>
          </button>
        </template>

        <div v-else class="recent-state" data-test="recent-searches-none">
          Your recent searches will show up here.
        </div>

        <div class="recent-divider" />
      </template>

      <!-- Try an example: always shown; the only content when recents are empty or failed. -->
      <div class="recent-title">Try an example</div>
      <button
        v-for="(example, idx) in CURATED_EXAMPLES"
        :key="idx"
        type="button"
        class="recent-item"
        data-test="recent-search-example"
        @click="emit('use-example', example)"
      >
        <span class="recent-q">{{ example }}</span>
        <span class="recent-meta recent-try">
          <ChevronRightIcon class="icon-xs" viewBox="0 0 24 24" />
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.empty {
  @apply mx-auto flex w-full max-w-[560px] flex-col items-center px-4 pb-5 pt-[60px] text-center;
}
.empty-title {
  @apply mb-1.5 text-[22px] font-bold tracking-[-0.01em] text-shade-880;
}
.empty-sub {
  @apply mb-7 max-w-[420px] text-sm leading-relaxed text-shade-800;
}
.recent-block {
  @apply w-full rounded-xl border border-tint-60 bg-tint-0 px-5 py-4 text-left shadow-sm;
}
.recent-title {
  @apply mb-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-shade-700;
}
.recent-state {
  @apply py-2 text-[13px] text-shade-700;
}
.recent-item {
  @apply flex w-full items-center justify-between border-t border-tint-40 py-2.5 text-left transition-colors;
}
/* No divider line directly under a section heading (mirrors the prototype). */
.recent-title + .recent-item {
  @apply border-t-0;
}
.recent-item:hover {
  @apply -mx-2 rounded-md bg-tint-20 px-2;
}
.recent-q {
  @apply min-w-0 flex-1 truncate pr-3 text-[13px] text-shade-820;
}
.recent-meta {
  @apply whitespace-nowrap text-[11.5px] tabular-nums text-shade-700;
}
.mode-tag {
  @apply mr-2 inline-block rounded px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.04em];
}
.mode-tag--smart {
  @apply bg-highlight-100 text-highlight-600;
}
.mode-tag--keyword {
  @apply bg-tint-40 text-shade-800;
}
.mode-tag--boolean {
  @apply bg-gold-bg-light text-gold-dark;
}
.recent-divider {
  @apply -mx-1 my-3 h-px bg-tint-40;
}
.recent-try {
  @apply inline-flex items-center text-shade-600;
}
.recent-item:hover .recent-try {
  @apply text-success-600;
}
.icon-xs {
  @apply h-3 w-3;
}
</style>
